/**
 * Add an event to all elements that match the provided selector.
 *
 * @template T The type of the element to add the event to.
 * @param selector The CSS selector to match.
 * @param eventName The event to listen for. Ex. click
 * @param callback The function to call on the event being triggered.
 */
export function addListenerToSelector(selector, eventName, callback) {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
        element.addEventListener(eventName, (e) => callback(e, element));
    });
}
/**
 * Queries an element and returns it if it exists. If it doesn't exist, it
 * throws an error.
 *
 * @template T The type of the element to query.
 * @param arg1 The parent element to query (or the selector if querying
 *   document).
 * @param arg2 The selector to query.
 * @returns The element that was found.
 * @throws Error An error if the element is not found.
 */
export function queryOrThrow(arg1, arg2) {
    const parent = typeof arg1 === "string" ? document : arg1;
    const selector = typeof arg1 === "string" ? arg1 : arg2 || "";
    const el = parent.querySelector(selector);
    if (!el)
        throw new Error(`${selector} not found`);
    return el;
}
/**
 * Executes a function once the DOM is ready.
 *
 * @param callback The function to execute.
 */
export function ready(callback) {
    if (document.readyState !== "loading") {
        callback();
    }
    else {
        document.addEventListener("DOMContentLoaded", () => {
            callback();
        });
    }
}
/**
 * Removes all the children from the provided element.
 *
 * @param element The element to remove the children of.
 */
export function removeChildren(element) {
    while (element.firstChild) {
        element.removeChild(element.firstChild);
    }
}
/**
 * Sets a property on all elements that match the provided selector. Note: these
 * are not CSS properties or necessarily HTML attributes.
 *
 * @template T The type of the element to set the property on.
 * @param selector The CSS selector to match.
 * @param property The property to set.
 * @param value The value to set the property to.
 */
export function setPropertyOnSelector(selector, property, value) {
    const elements = document.querySelectorAll(selector);
    elements.forEach((element) => {
        element.setAttribute(property, value);
    });
}
/**
 * Waits for an element to exist before executing a callback.
 *
 * @param containerSelector The selector for the container of the element to
 *   watch for.
 * @param waitingForSelector The selector for the element to watch for.
 * @param callback The function to execute once the element exists.
 * @param runOnce If true, the callback will only be executed once.
 * @returns False if the container element does not exist. Undefined otherwise.
 */
export function waitFor(containerSelector, waitingForSelector, callback, runOnce = false) {
    const observerOptions = {
        childList: true,
        subtree: true,
    };
    const elementToWatch = document.querySelector(containerSelector);
    if (!elementToWatch) {
        return false;
    }
    if (elementToWatch.querySelector(waitingForSelector)) {
        callback();
    }
    const observer = new MutationObserver(() => {
        if (!elementToWatch.querySelector(waitingForSelector))
            return;
        if (runOnce) {
            observer.disconnect();
        }
        callback();
    });
    observer.observe(elementToWatch, observerOptions);
}
//# sourceMappingURL=dom.js.map